/**
 * Says what is wrong when Lou is not fully reachable, and what still works.
 *
 * Two failures look identical to a user — nothing happens — but mean very
 * different things, so they get different words:
 *
 * - **realtime down, HTTP up.** Messages still send and are still answered;
 *   the reply simply will not stream in. Reopening the conversation shows it.
 *   Worth saying, not worth blocking on.
 * - **the assistant unreachable.** Sending cannot work, so the composer is
 *   disabled and says why rather than accepting text that goes nowhere.
 *
 * Silence is the one thing not to do: a user who gets no reply and no
 * explanation assumes the product is broken, not that one service is down.
 */

import React from "react";
import { FiAlertTriangle, FiWifiOff } from "react-icons/fi";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import "./ConnectionNotice.scss";

/** States where the connection is genuinely lost rather than settling. */
const LOST = new Set(["disconnected", "suspended", "failed", "closed"]);

const ConnectionNotice = () => {
  const { realtimeConnectionState, isReachable } = useAssistantAbly();

  if (isReachable === false) {
    return (
      <div className="lou-notice lou-notice--down" role="status">
        <FiAlertTriangle size={15} aria-hidden />
        <span>
          Lou is unavailable right now. Your past conversations are still here.
        </span>
      </div>
    );
  }

  if (LOST.has(realtimeConnectionState)) {
    return (
      <div className="lou-notice lou-notice--degraded" role="status">
        <FiWifiOff size={15} aria-hidden />
        <span>
          Reconnecting. You can still send messages — replies will appear once
          the connection is back.
        </span>
      </div>
    );
  }

  return null;
};

export default ConnectionNotice;
